import React, { useEffect, useState } from 'react';
import ConfirmModal from '../../common/ConfirmModal/ConfirmModal';
import FormInput from '../../common/FormInput/FormInput';
import FormSelect from '../../common/FormSelect/FormSelect';
import { formatCurrency } from '../../../utils';
import styles from './RegisterPixChargeModal.module.css';

export type PixChargeStatus = 'PAID' | 'PENDING' | 'EXPIRED' | 'CANCELED';

const STATUS_OPTIONS: Array<{ value: PixChargeStatus; label: string }> = [
  { value: 'PAID', label: 'Pago' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'EXPIRED', label: 'Vencido' },
  { value: 'CANCELED', label: 'Cancelado' },
];

const getBrazilTodayDateOnly = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10);
};

interface RegisterPixChargeModalProps {
  open: boolean;
  loading: boolean;
  amount: number;
  planName: string | null;
  onConfirm: (input: { status: PixChargeStatus; chargeDate: string }) => void;
  onCancel: () => void;
}

const RegisterPixChargeModal: React.FC<RegisterPixChargeModalProps> = ({
  open,
  loading,
  amount,
  planName,
  onConfirm,
  onCancel,
}) => {
  const [status, setStatus] = useState<PixChargeStatus>('PAID');
  const [chargeDate, setChargeDate] = useState(getBrazilTodayDateOnly);

  useEffect(() => {
    if (!open) {
      return;
    }

    setStatus('PAID');
    setChargeDate(getBrazilTodayDateOnly());
  }, [open]);

  const dateLabel = status === 'PAID'
    ? 'Data do pagamento'
    : status === 'EXPIRED'
      ? 'Data de vencimento'
      : 'Data da cobrança';

  return (
    <ConfirmModal
      open={open}
      variant="info"
      icon="receipt_long"
      title="Registrar cobrança"
      message={planName
        ? `Lançamento de Pix direto na conta da mensalidade do plano ${planName}.`
        : 'Lançamento de Pix direto na conta da mensalidade.'}
      confirmLabel={loading ? 'Aguarde...' : 'Registrar'}
      cancelLabel="Cancelar"
      loading={loading}
      alignment="left"
      width={440}
      onConfirm={() => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(chargeDate)) {
          return;
        }
        onConfirm({ status, chargeDate });
      }}
      onCancel={onCancel}
    >
      <div className={styles.fields}>
        <p className={styles.value}>
          <span>Valor da mensalidade</span>
          <strong>{formatCurrency(amount)}</strong>
        </p>
        <FormSelect
          id="pix-charge-status"
          label="Status"
          options={STATUS_OPTIONS}
          value={status}
          onChange={(event) => setStatus(event.target.value as PixChargeStatus)}
          disabled={loading}
        />
        <FormInput
          id="pix-charge-date"
          label={dateLabel}
          type="date"
          value={chargeDate}
          onChange={(event) => setChargeDate(event.target.value)}
          disabled={loading}
          required
        />
      </div>
    </ConfirmModal>
  );
};

export default RegisterPixChargeModal;
